/* eslint-disable bsky-internal/avoid-unwrapped-text */
import {
  type AnchorHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react'

import {useSemanticTokens} from '#/wsocial/service/design-system'

/**
 * Themed onboarding text primitives.
 * Consume `useSemanticTokens().onboarding` so colours track the active theme.
 * Typography sizes are baked in; callers only control content and optional
 * layout overrides via `style`.
 */

type TextProps = {
  children: ReactNode
  /** When true, use `onboarding.textLow` instead of `onboarding.textMedium`. */
  muted?: boolean
  style?: CSSProperties
}

export function Heading({
  children,
  style,
}: {
  children: ReactNode
  style?: CSSProperties
}) {
  const tokens = useSemanticTokens()
  return (
    <h1
      style={{
        fontSize: 30,
        fontWeight: 700,
        lineHeight: '35px',
        color: tokens.onboarding.textPrimary,
        margin: 0,
        textAlign: 'center',
        ...style,
      }}>
      {children}
    </h1>
  )
}

export function BodyText({children, muted, style}: TextProps) {
  const tokens = useSemanticTokens()
  return (
    <p
      style={{
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '23px',
        color: muted ? tokens.onboarding.textLow : tokens.onboarding.textMedium,
        margin: 0,
        ...style,
      }}>
      {children}
    </p>
  )
}

export function SmallText({children, muted, style}: TextProps) {
  const tokens = useSemanticTokens()
  return (
    <p
      style={{
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '17px',
        color: muted ? tokens.onboarding.textLow : tokens.onboarding.textMedium,
        margin: 0,
        ...style,
      }}>
      {children}
    </p>
  )
}

export function AccentLink({
  children,
  style,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const tokens = useSemanticTokens()
  return (
    <a
      {...rest}
      style={{
        color: tokens.onboarding.btnPrimaryBg,
        textDecoration: 'none',
        ...style,
      }}>
      {children}
    </a>
  )
}
